import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Video as VideoIcon, Lock, Globe, Loader2, Mic, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import VideoRoom from '@/components/VideoRoom';

const FREE_GROUP_ID = '0ad0f063-9b6c-4c70-a63d-337fab466af1';

export default function DashboardPage() {
  const { user, hasSub } = useAuth();
  const navigate = useNavigate();
  const [publicGroups, setPublicGroups] = useState([]);
  const [privateGroups, setPrivateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [joined, setJoined] = useState(null); // { group, withVideo, displayName }
  const [refreshSecs, setRefreshSecs] = useState(30);
  const intervalRef = useRef(null);

  // Pre-join dialog state (only shown if any pref is 'ask')
  const [prejoin, setPrejoin] = useState(null); // { group, askVideo, askNickname, withVideo, showNickname }

  useEffect(() => {
    if (!hasSub) {
      toast.error('Aktivt abonnement kræves');
      navigate('/payment');
      return;
    }
    fetchGroups();
    // Also fetch params for refresh interval
    (async () => {
      try {
        const { data } = await api.get('/parameters');
        const s = parseInt(data?.seconds_update_groups) || 30;
        setRefreshSecs(Math.max(5, s));
      } catch (e) { console.debug('parameters fetch failed, keeping default 30s:', e); }
    })();
    // eslint-disable-next-line
  }, [hasSub]);

  // Auto-refresh every refreshSecs seconds while on dashboard (not in a video room)
  useEffect(() => {
    if (joined || !hasSub) return;
    intervalRef.current = setInterval(() => { fetchGroups(true); }, refreshSecs * 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line
  }, [refreshSecs, joined, hasSub]);

  const fetchGroups = async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const [pub, priv] = await Promise.all([
        api.get('/groups?private=false'),
        api.get('/groups?private=true'),
      ]);
      setPublicGroups(pub.data);
      setPrivateGroups(priv.data);
    } catch (err) {
      if (err.response?.status === 402) {
        navigate('/payment');
      } else if (!silent) {
        toast.error('Kunne ikke hente grupper');
      }
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  };

  // Given the resolved prefs, perform the actual join
  const doJoin = async (group, withVideo, showNickname) => {
    const displayName = showNickname && user?.nickname ? user.nickname : 'Deltager';
    try {
      const { data } = await api.post(`/groups/${group.id}/join`, {
        with_video: withVideo,
        display_name: displayName,
      });
      setJoined({
        group: data.group,
        withVideo,
        displayName,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke tilslutte gruppe');
    }
  };

  const handleTilslut = (group) => {
    // Group-level audio_only override — user's show_video preference does not matter
    if (group.audio_only) {
      const showNickname = user?.show_nickname === 'always';
      doJoin(group, false, showNickname);
      return;
    }

    const askVideo = user?.show_video === 'ask';
    const askNickname = user?.show_nickname === 'ask';

    if (askVideo || askNickname) {
      setPrejoin({
        group,
        askVideo,
        askNickname,
        // sensible defaults for the dialog
        withVideo: user?.show_video !== 'never',
        showNickname: user?.show_nickname !== 'never',
      });
      return;
    }
    // Auto-resolve based on saved prefs
    const withVideo = user?.show_video === 'always';
    const showNickname = user?.show_nickname === 'always';
    doJoin(group, withVideo, showNickname);
  };

  const confirmPrejoin = () => {
    // If a field was not asked, fall back to user pref
    const withVideo = prejoin.askVideo ? prejoin.withVideo : user?.show_video === 'always';
    const showNickname = prejoin.askNickname ? prejoin.showNickname : user?.show_nickname === 'always';
    const g = prejoin.group;
    setPrejoin(null);
    doJoin(g, withVideo, showNickname);
  };

  const leaveGroup = async () => {
    if (!joined) return;
    try {
      await api.post(`/groups/${joined.group.id}/leave`);
    } catch (e) { console.debug('leave group error (non-blocking):', e); }
    setJoined(null);
    // Ensure we always land on Overblik (dashboard) — critical on mobile
    navigate('/dashboard');
    fetchGroups();
  };

  if (joined) {
    // Show the connected-users counter ONLY when a supervisor is in the free group.
    const isSupervisorInFreeGroup = !!user?.this_is_supervisor && joined.group.id === FREE_GROUP_ID;
    return (
      <VideoRoom
        groupId={joined.group.id}
        groupName={joined.group.name}
        displayName={joined.displayName}
        myNickname={user?.nickname || user?.first_name || 'Deltager'}
        withVideo={joined.withVideo}
        showConnectedCount={isSupervisorInFreeGroup}
        onLeave={leaveGroup}
      />
    );
  }

  return (
    <div className="px-6 md:px-12 py-10 md:py-16 pb-24 md:pb-16 max-w-7xl">
      <div className="mb-12 animate-fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Velkommen tilbage, {user?.nickname || user?.first_name}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Åbne rum <span className="text-volt">for dig</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground hidden sm:inline">Opdaterer hver {refreshSecs}s</span>
          <Button
            variant="outline"
            onClick={() => fetchGroups(true)}
            disabled={refreshing || loading}
            className="border-border hover:border-volt/40 gap-2"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Opdater
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="public" className="w-full" data-testid="dashboard-tabs">
          <TabsList className="bg-[#18181B] border border-border mb-6">
            <TabsTrigger value="public" data-testid="dashboard-tab-public">
              <Globe className="w-4 h-4 mr-2" /> Offentlige grupper
            </TabsTrigger>
            <TabsTrigger value="private" data-testid="dashboard-tab-private">
              <Lock className="w-4 h-4 mr-2" /> Private grupper
            </TabsTrigger>
          </TabsList>

          <TabsContent value="public">
            <GroupsGrid groups={publicGroups} onJoin={handleTilslut} testId="public-groups-grid" emptyText="Ingen åbne grupper tilgængelige." />
          </TabsContent>
          <TabsContent value="private">
            <GroupsGrid groups={privateGroups} onJoin={handleTilslut} testId="private-groups-grid" emptyText="Ingen private grupper tilgængelige." />
          </TabsContent>
        </Tabs>
      )}

      {/* Pre-join dialog: only shown if user has 'ask' on video or nickname */}
      <Dialog open={!!prejoin} onOpenChange={(v) => !v && setPrejoin(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Før du tilslutter</DialogTitle>
            <DialogDescription>Vælg hvordan du vil deltage i {prejoin?.group?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {prejoin?.askVideo && (
              <div className="flex items-start gap-3 p-4 rounded-md border border-border bg-[#09090B]">
                <Checkbox id="pj-video" checked={prejoin.withVideo}
                  onCheckedChange={(v) => setPrejoin({ ...prejoin, withVideo: !!v })}
                  data-testid="prejoin-video-checkbox" />
                <div className="flex-1">
                  <label htmlFor="pj-video" className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <VideoIcon className="w-4 h-4 text-volt" /> Tænd kamera
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {prejoin.withVideo ? 'Video + mikrofon (hvis kamera findes)' : 'Kun mikrofon'}
                  </p>
                </div>
              </div>
            )}
            {prejoin?.askNickname && (
              <div className="flex items-start gap-3 p-4 rounded-md border border-border bg-[#09090B]">
                <Checkbox id="pj-nick" checked={prejoin.showNickname}
                  onCheckedChange={(v) => setPrejoin({ ...prejoin, showNickname: !!v })}
                  data-testid="prejoin-nickname-checkbox" />
                <div className="flex-1">
                  <label htmlFor="pj-nick" className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    Vis mit kaldenavn
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {prejoin.showNickname ? `Vises som "${user?.nickname || 'Deltager'}"` : 'Vises som "Deltager"'}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrejoin(null)} className="border-border">Annullér</Button>
            <Button onClick={confirmPrejoin} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="prejoin-confirm-btn">
              Tilslut
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function GroupsGrid({ groups, onJoin, testId, emptyText }) {
  if (!groups.length) {
    return <div className="text-center py-24 text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid={testId}>
      {groups.map((g, i) => (
        <div
          key={g.id}
          className="group relative rounded-lg border border-border bg-[#18181B] p-6 hover:border-volt/40 hover:-translate-y-[2px] hover:shadow-lg transition-all duration-300 animate-fade-up"
          style={{ animationDelay: `${i * 40}ms` }}
          data-testid={`group-card-${g.id}`}
        >
          <div className="mb-6">
            <div
              className="inline-flex items-center px-2 py-0.5 rounded-md bg-orange-500 text-black text-[9px] font-bold uppercase tracking-wider mb-3 max-w-full truncate"
              data-testid={`group-privacy-badge-${g.id}`}
            >
              {g.private ? 'PRIVAT GRUPPE' : 'Åben gruppe'}
            </div>
            <h3 className="font-display text-xl font-bold truncate">{g.name}</h3>
          </div>

          <div className="flex flex-col gap-2 text-xs text-muted-foreground mb-6">
            <span data-testid={`group-audio-label-${g.id}`} className={g.audio_only ? 'text-amber-400' : 'text-volt'}>
              {g.audio_only ? 'Anonymt – kun mikrofon' : 'Video er muligt'}
            </span>
          </div>

          <Button
            onClick={() => onJoin(g)}
            className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
            data-testid={`join-group-btn-${g.id}`}
          >
            Deltag nu
          </Button>
        </div>
      ))}
    </div>
  );
}
